import { useContext } from "react";
import { SettingsContext } from "../context/settingsContext";
import { ErrorResponse } from "../types/generic";
import { InstituteTypeItem, InstituteType } from "../types/institutes";
import { PostCodeItem } from "../types/postCodes";

type QueryParams = Record<string, string | number | null | undefined>;

export class ApiError extends Error {
  status: number;
  data?: ErrorResponse;

  constructor(status: number, statusText: string, data?: ErrorResponse) {
    super(`Request failed with status ${status} ${statusText}`);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export default function useApi() {
  const { apiKey, apiUrl } = useContext(SettingsContext);
  const baseUrl = apiUrl || process.env.REACT_APP_API_URL || "";
  const token = apiKey || process.env.REACT_APP_API_KEY;

  async function get<T>(path: string, params: QueryParams = {}): Promise<T> {
    const searchParams = new URLSearchParams();
    // skip empty params like axios does
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const query = searchParams.toString();
    const url = `${baseUrl.replace(/\/+$/, "")}${path}${query ? `?${query}` : ""}`;

    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) {
      headers["X-AUTH-TOKEN"] = token;
    }

    const res = await fetch(url, { headers });
    if (!res.ok) {
      const data = await res.json().catch(() => undefined);
      throw new ApiError(res.status, res.statusText, data);
    }
    return res.json();
  }

  async function fetchInstituteTypes() {
    const res = await get<{ data: InstituteTypeItem[] }>("/institute-types");
    return res.data;
  }

  async function fetchInstitutes(
    instituteType: number | null,
    postCodeId: number | null
  ) {
    const res = await get<{ data: InstituteType[] }>("/v2/institutes", {
      type_id: instituteType,
      zip_code_id: postCodeId,
    });
    return res.data;
  }

  async function fetchPostCodes(zipCode: string) {
    const res = await get<{ data: PostCodeItem[] }>("/post-codes", {
      zip_code: zipCode,
    });
    return res.data;
  }

  return {
    fetchInstituteTypes,
    fetchInstitutes,
    fetchPostCodes,
  };
}
